import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { User, getUser, authService, RegisterOptions } from '../services/auth';
import { notificationService } from '../services/notification';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (accessToken: string) => Promise<void>;
  register: (
    name: string,
    email: string,
    password: string,
    options?: RegisterOptions,
  ) => Promise<boolean>;
  updateUser: (userData: Partial<User>) => void;
  logout: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already logged in
    const loadUser = async () => {
      try {
        const userData = getUser();
        if (userData) {
          setUser(userData);
          try {
            const currentUser = await authService.getCurrentUser();
            if (currentUser) {
              setUser(currentUser);
            }
          } catch (fetchErr) {
            console.error('Failed to refresh user data:', fetchErr);
            authService.logout();
            setUser(null);
          } finally {
            setIsLoading(false);
          }
        } else {
          try {
            const currentUser = await authService.getCurrentUser();
            if (currentUser) {
              setUser(currentUser);
            }
          } catch (fetchErr) {
            console.error('Failed to fetch current user:', fetchErr);
          } finally {
            setIsLoading(false);
          }
        }
      } catch (err) {
        console.error('Failed to load user:', err);
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authService.login({ email, password });
      setUser(response.user);
      notificationService.syncPushRegistration({ force: true }).catch(console.error);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    name: string,
    email: string,
    password: string,
    options?: RegisterOptions,
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      const success = await authService.register(
        { name, email, password },
        options,
      );
      return success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async (accessToken: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authService.loginWithGoogle(accessToken);
      setUser(response.user);
      notificationService.syncPushRegistration({ force: true }).catch(console.error);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      // Persist to localStorage
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
    }
  };

  const logout = async () => {
    await notificationService.unregisterPushToken();
    await authService.logout();
    setUser(null);
    // Redirect to home page after logout
    window.location.href = '/';
  };

  // Check if token exists to determine authentication status
  const isAuthenticated = !!user;

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    login,
    loginWithGoogle,
    register,
    updateUser,
    logout,
    error
  }), [user, isAuthenticated, isLoading, error, login, loginWithGoogle, register, logout, updateUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
